import React from 'react';
import { LockIcon } from 'lucide-react';
import { FilterChip } from './FilterChip';
import { Card, SectionTitle } from './ui/Card';
import { profileFieldsForSport } from '../data/sportProfileFields';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { SportId } from '../types';
import { displaySportField } from '../utils/sportFields';

/**
 * Categorical / profile fields of the selected sport only (belt, style, modality,
 * weight category, federation…). Read from the central catalogue; never charted.
 * Derived fields (time at current belt) are read-only.
 */
export function SportProfileFields({
  sport
}: {
  sport: SportId;
}) {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const {
    sportFields,
    setSportField
  } = useMetrics();
  const fields = profileFieldsForSport(sport);
  if (fields.length === 0) return null;
  return <section className="mt-3" aria-labelledby="sport-profile" data-id="element-1471" data-mp="3srmwxu99zvi4">
      <SectionTitle data-id="element-1472" data-mp="3st7vduypnyl9">
        <span id="sport-profile" data-id="element-1473" data-mp="994zh26yc46p">{t('sport.profile.title')}</span>
      </SectionTitle>
      <Card className="px-2" data-id="element-1474" data-mp="971zpqfzywos">
        <ul data-id="element-1475" data-mp="e7g5t2y1fwk6">
          {fields.map(field => {
          const labelId = `field-${field.id.replace(':', '-')}`;
          const optional = field.type !== 'tenure' && field.optional;
          return <li key={field.id} className="border-b border-sand-line py-2 last:border-b-0" data-id="element-1476" data-mp="nyuf22fskaqi">
                <p id={labelId} className="flex items-center gap-1 text-[13px] font-medium text-deep" data-id="element-1477" data-mp="s05vbhcrzjm1">
                  {tl(field.label)}
                  {optional && <span className="text-[11px] font-normal text-muted" data-id="element-1478" data-mp="11xtsedxut37d">· {t('sport.field.optional')}</span>}
                  {field.type === 'tenure' && <LockIcon size={11} className="text-muted" aria-hidden="true" data-id="element-1479" data-mp="16uaru4ai73zr" />}
                </p>

                {field.type === 'select' && <ul role="radiogroup" aria-labelledby={labelId} className="mt-1 flex flex-wrap gap-1" data-id="element-1480" data-mp="38ojry9wxpmgk">
                    {field.options.map(option => <li key={option.id} data-id="element-1481" data-mp="2vor2qfwmyocp">
                        <FilterChip label={tl(option.label)} active={sportFields[field.id] === option.id} onClick={() => setSportField(field.id, option.id)} data-id="element-1482" data-mp="2vq27ahxwo04v" />
                      </li>)}
                  </ul>}

                {field.type === 'text' && <input type="text" aria-labelledby={labelId} value={sportFields[field.id] ?? ''} placeholder={t('sport.field.textPlaceholder')} onChange={event => setSportField(field.id, event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep placeholder:text-muted focus:border-gold focus:outline-none" data-id="element-1483" data-mp="3emk59h3ckw9k" />}

                {field.type === 'date' && <input type="date" aria-labelledby={labelId} value={sportFields[field.id] ?? ''} onChange={event => setSportField(field.id, event.target.value)} className="bee-num mt-1 h-11 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-gold focus:outline-none" data-id="element-1484" data-mp="ps0qta5zstqi" />}

                {field.type === 'tenure' && <div className="mt-[2px] flex items-baseline justify-between gap-2" data-id="element-1485" data-mp="3p98duytbd5s0">
                    <span className="text-[11px] leading-snug text-muted" data-id="element-1486" data-mp="3t90snt9aw85t">{t('sport.field.tenureHint')}</span>
                    <span className="bee-num shrink-0 text-[14px] font-bold text-deep" data-id="element-1487" data-mp="3fb2g8tejilsy">
                      {displaySportField(field, sportFields, locale, tl) || t('metrics.notSet')}
                    </span>
                  </div>}
              </li>;
        })}
        </ul>
      </Card>
      <p className="mt-1 flex items-start gap-1 px-1 text-[11px] leading-snug text-muted" data-id="element-1488" data-mp="mg2kn81uh8yl">
        <LockIcon size={12} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-1489" data-mp="hnj3y27v1xpk" />
        {t('sport.profile.note')}
      </p>
    </section>;
}